import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Avatar,
  Chip,
  Grid,
  Card,
  CardContent,
  Alert,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import {
  Person as PersonIcon,
  MonitorWeight as MonitorWeightIcon,
  MedicalServices as MedicalServicesIcon,
  Favorite as FavoriteIcon,
  Science as ScienceIcon,
  DirectionsRun as DirectionsRunIcon,
  Restaurant as RestaurantIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  LocationOn as LocationOnIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import patientsService from '../../services/patientsService';

// Componentes individuales
import DatosGeneralesView from './DatosGeneralesView';
import MedicionesAntropometricasView from './MedicionesAntropometricasView';
import HistoriaClinicaView from './HistoriaClinicaView';
import SignosVitalesView from './SignosVitalesView';
import HabitosView from './HabitosView';
import ActividadFisicaView from './ActividadFisicaView';
import DatosLaboratorioView from './DatosLaboratorioView';
import DiarioAlimenticioView from './DiarioAlimenticioView';
import CapturaExpedienteDialog from './CapturaExpedienteDialog';

const ExpedienteClinico = ({ patientId, onNavigate }) => {
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [captureKind, setCaptureKind] = useState(null);
  const [captureRecord, setCaptureRecord] = useState(null);
  const [captureMenuAnchor, setCaptureMenuAnchor] = useState(null);

  // Real assembled clinical record. datos_generales is always present (built
  // from Patient + AuthUser); the clinical tabs render real data or honest
  // empty states. No mock/demo fallback. Reusable so capture can re-fetch.
  const loadExpediente = useCallback(async () => {
    if (!patientId) {
      setLoading(false);
      setSelectedPatient(null);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const expediente = await patientsService.getExpediente(patientId);
      setSelectedPatient(expediente);
    } catch (err) {
      console.error('Error fetching expediente:', err);
      setSelectedPatient(null);
      setError('No se pudo cargar el expediente. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { loadExpediente(); }, [loadExpediente]);

  // Open capture in create (record=null) or edit (record set) mode.
  const openCapture = useCallback((kind, record = null) => {
    setCaptureRecord(record);
    setCaptureKind(kind);
  }, []);

  const DELETE_FN = {
    anthropometrics: (rid) => patientsService.deleteAnthropometrics(patientId, rid),
    vital_signs: (rid) => patientsService.deleteVitalSigns(patientId, rid),
    laboratory: (rid) => patientsService.deleteLaboratory(patientId, rid),
  };

  const handleDelete = useCallback(async (kind, recordId) => {
    const fn = DELETE_FN[kind];
    if (!fn || !recordId) return;
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm('¿Eliminar este registro? Esta acción no se puede deshacer.')) return;
    try {
      await fn(recordId);
      await loadExpediente();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e.message || 'No se pudo eliminar el registro.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, loadExpediente]);

  const CAPTURE_OPTIONS = [
    { kind: 'anthropometrics', label: '➕ Nueva medición' },
    { kind: 'vital_signs', label: '❤️ Signos vitales' },
    { kind: 'laboratory', label: '🔬 Laboratorio' },
    { kind: 'medical_history', label: '📋 Historia clínica' },
    { kind: 'habits', label: '🍽️ Hábitos' },
    { kind: 'physical_activity', label: '🏃 Actividad física' },
  ];

  // Tabs del expediente
  const tabs = [
    { id: 0, label: 'Datos Generales', icon: <PersonIcon />, component: DatosGeneralesView },
    { id: 1, label: 'Antropometría', icon: <MonitorWeightIcon />, component: MedicionesAntropometricasView },
    { id: 2, label: 'Historia Clínica', icon: <MedicalServicesIcon />, component: HistoriaClinicaView },
    { id: 3, label: 'Laboratorios', icon: <ScienceIcon />, component: DatosLaboratorioView },
    { id: 4, label: 'Signos Vitales', icon: <FavoriteIcon />, component: SignosVitalesView },
    { id: 5, label: 'Hábitos', icon: <RestaurantIcon />, component: HabitosView },
    { id: 6, label: 'Actividad Física', icon: <DirectionsRunIcon />, component: ActividadFisicaView },
    { id: 7, label: 'Diario', icon: <RestaurantIcon />, component: DiarioAlimenticioView },
  ];

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Show loading state
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ color: '#006847', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Cargando expediente clínico...
          </Typography>
        </Box>
      </Container>
    );
  }

  // If no patient ID provided, show message to select a patient
  if (!patientId && !selectedPatient) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 4,
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            }}
          >
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                bgcolor: '#006847',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                mb: 3,
              }}
            >
              <MedicalServicesIcon sx={{ fontSize: 60, color: 'white' }} />
            </Box>

            <Typography variant="h4" fontWeight={700} gutterBottom color="#2c3e50">
              Expediente Clínico
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
              Para ver el expediente clínico completo de un paciente, primero debes seleccionarlo
              desde la sección de <strong>Gestión de Pacientes</strong>.
            </Typography>

            <Button
              variant="contained"
              size="large"
              startIcon={<PeopleIcon />}
              onClick={() => {
                // Navigate to patients section
                if (onNavigate) {
                  onNavigate('patients');
                } else {
                  // Fallback: try to find the sidebar navigation
                  const event = new CustomEvent('navigate', { detail: { view: 'patients' } });
                  window.dispatchEvent(event);
                }
              }}
              sx={{
                bgcolor: '#006847',
                '&:hover': { bgcolor: '#005035' },
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
              }}
            >
              Ir a Gestión de Pacientes
            </Button>

            <Divider sx={{ my: 4 }} />

            <Typography variant="body2" color="text.secondary">
              Desde la sección de pacientes podrás:
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2, flexWrap: 'wrap' }}>
              <Chip icon={<PersonIcon />} label="Ver lista de pacientes" variant="outlined" />
              <Chip icon={<EditIcon />} label="Agregar nuevo paciente" variant="outlined" />
              <Chip icon={<MedicalServicesIcon />} label="Abrir expediente" variant="outlined" />
            </Box>
          </Paper>
        </motion.div>
      </Container>
    );
  }

  // Honest empty/error state — no demo data, plus a way back to Pacientes.
  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<PeopleIcon />}
          onClick={() => onNavigate && onNavigate('patients')}
        >
          Volver a Pacientes
        </Button>
      </Container>
    );
  }

  // If no patient data, show message
  if (!selectedPatient) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="info">
          No hay datos de paciente disponibles
        </Alert>
      </Container>
    );
  }

  // Safe accessors — real expedientes start mostly empty (no measurements /
  // vitals captured yet). Header must render without crashing on absent data.
  const dg = selectedPatient.datos_generales || {};
  const med0 = (selectedPatient.mediciones_antropometricas || [])[0] || null;
  const sv0 = (selectedPatient.signos_vitales || [])[0] || null;
  const dash = (v, suffix = '') => (v === null || v === undefined || v === '' ? '—' : `${v}${suffix}`);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header del Expediente */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            background: 'linear-gradient(135deg, #006847 0%, #00a86b 100%)',
            color: 'white',
            borderRadius: 4,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: 'white',
                  color: '#006847',
                  fontSize: '2.5rem',
                  fontWeight: 'bold',
                  border: '4px solid rgba(255,255,255,0.3)',
                }}
              >
                {(dg.nombre_completo || '?').charAt(0)}
              </Avatar>
            </Grid>

            <Grid item xs>
              <Typography variant="h4" fontWeight="700" gutterBottom>
                {dg.nombre_completo || 'Sin nombre'}
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                {dg.edad != null && (
                  <Chip
                    label={`${dg.edad} años`}
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                  />
                )}
                {dg.sexo && (
                  <Chip
                    label={dg.sexo === 'femenino' ? 'Femenino' : dg.sexo === 'masculino' ? 'Masculino' : 'Otro'}
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                  />
                )}
                {med0?.imc != null && (
                  <Chip
                    label={`IMC: ${med0.imc}`}
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                  />
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PhoneIcon fontSize="small" />
                  <Typography variant="body2">{dash(dg.telefono)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EmailIcon fontSize="small" />
                  <Typography variant="body2">{dash(dg.email)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <WhatsAppIcon fontSize="small" />
                  <Typography variant="body2">{dash(dg.whatsapp)}</Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={(e) => setCaptureMenuAnchor(e.currentTarget)}
                  sx={{
                    bgcolor: 'white',
                    color: '#006847',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                    fontWeight: 600,
                  }}
                >
                  Capturar datos
                </Button>
                <Menu
                  anchorEl={captureMenuAnchor}
                  open={Boolean(captureMenuAnchor)}
                  onClose={() => setCaptureMenuAnchor(null)}
                >
                  {CAPTURE_OPTIONS.map((opt) => (
                    <MenuItem
                      key={opt.kind}
                      onClick={() => { openCapture(opt.kind); setCaptureMenuAnchor(null); }}
                    >
                      {opt.label}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            </Grid>
          </Grid>

          {/* Alertas IA */}
          {sv0?.alertas_ia?.recomendacion_urgente && (
            <Alert
              severity="error"
              icon={<WarningIcon />}
              sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.95)', fontWeight: 600 }}
            >
              <strong>ALERTA MÉDICA:</strong> {sv0.alertas_ia.recomendacion_urgente}
            </Alert>
          )}
        </Paper>

        {/* Resumen Ejecutivo con Indicadores Clave */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%', borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <MonitorWeightIcon sx={{ color: '#006847', mr: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Peso Actual
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="700">
                  {dash(med0?.peso_kg, ' kg')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {med0?.interpretacion_imc || 'Sin medición'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%', borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingUpIcon sx={{ color: '#FF6B35', mr: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Meta de Peso
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="700">
                  {med0?.peso_kg != null
                    ? `${(med0.peso_kg * 0.9).toFixed(1)} kg`
                    : '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  -10% recomendado
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%', borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <FavoriteIcon sx={{ color: '#E63946', mr: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Presión Arterial
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="700">
                  {sv0?.presion_sistolica_mmHg != null && sv0?.presion_diastolica_mmHg != null
                    ? `${sv0.presion_sistolica_mmHg}/${sv0.presion_diastolica_mmHg}`
                    : '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {sv0?.clasificacion_presion || 'Sin registro'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%', borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ScienceIcon sx={{ color: '#457B9D', mr: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Glucosa
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="700">
                  {dash(sv0?.glucosa_capilar_mgdl, ' mg/dL')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {sv0?.momento_medicion || 'Sin registro'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs de Navegación */}
        <Paper elevation={0} sx={{ borderRadius: 3, mb: 3 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                minHeight: 72,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
              },
              '& .Mui-selected': {
                color: '#006847',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#006847',
                height: 3,
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                icon={tab.icon}
                label={tab.label}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Paper>

        {/* Contenido del Tab Actual */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {tabs[currentTab].component &&
              React.createElement(tabs[currentTab].component, {
                paciente: selectedPatient,
                onCapture: openCapture,
                onDelete: handleDelete,
              })
            }
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {/* Objetivo del paciente */}
      {dg.objetivo_principal && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 3,
            borderRadius: 3,
            bgcolor: '#F8F9FA',
            borderLeft: '4px solid #006847',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <strong>Objetivo principal:</strong> {dg.objetivo_principal}
          </Typography>
        </Paper>
      )}

      <CapturaExpedienteDialog
        open={Boolean(captureKind)}
        kind={captureKind}
        record={captureRecord}
        patientId={patientId}
        onClose={() => { setCaptureKind(null); setCaptureRecord(null); }}
        onSaved={loadExpediente}
      />
    </Container>
  );
};

export default ExpedienteClinico;
